import React from 'react';
import { Box, Typography } from '@lucid/lego';
import type { CartItem } from '@lucid/cart';
import VehicleCard from '../VehicleCard/VehicleCard';
import styles from './VehicleList.module.scss';

type VehicleListLabels = {
  emptyCart?: string;
};

export type VehicleListProps = {
  theme: 'dark' | 'light';
  labels?: VehicleListLabels;
  isLoading: boolean;
  items: CartItem[];
  currencyCode?: string;
  expandedItems: Record<string, boolean>;
  onToggle: (id: string) => void;
  customerId: string;
};

/**
 * Renders the list of vehicle cards in the cart, including loading and empty states.
 * @component
 * @param {VehicleListProps} props
 * @returns {JSX.Element}
 * @example
 * <VehicleList theme="light" isLoading={false} items={items} currencyCode="USD" expandedItems={{}} onToggle={() => {}} customerId="123" />
 */
const VehicleList: React.FC<VehicleListProps> = ({
  theme,
  labels,
  isLoading,
  items,
  currencyCode,
  expandedItems,
  onToggle,
  customerId,
}) => (
  <Box
    className={styles.vehicleList}
    data-testid="checkout-vehicle-list"
    background="white"
    pl={16}
    pr={16}
    pb={24}
  >
    {isLoading && (
      <Box pt={24} pb={24}>
        <Typography variant="body" size="small" color="gray-3" theme={theme}>
          Loading...
        </Typography>
      </Box>
    )}
    {!isLoading && items.length === 0 && (
      <Box pt={24} pb={24}>
        <Typography
          variant="body"
          size="small"
          color="gray-3"
          theme={theme}
          data-testid="checkout-empty-state"
        >
          {labels?.emptyCart ?? 'Your cart is empty.'}
        </Typography>
      </Box>
    )}
    {items.map((item, index) => {
      const id = item.itemId ?? item.itemCode ?? String(index);
      return (
        <VehicleCard
          key={id}
          item={item}
          currencyCode={currencyCode}
          theme={theme}
          isExpanded={expandedItems[id] ?? false}
          onToggle={() => onToggle(id)}
          customerId={customerId}
        />
      );
    })}
  </Box>
);

export default VehicleList;
